<template>
    <AddFileSection
        :id="AddFileFormSectionIds.FILE_NOTIFICATION"
        sectionHeading="Add notification recipients"
    >
        <div class="mb-3 mt-1 text-content-md text-content-primary">
            Select who gets notified when clients complete tasks. The file opener is notified by default.
        </div>
        <DsMultiSelect
            v-model="addFileStore.notificationUsers"
            id="file-notification-users"
            :disabled="isFetching"
            disableSelectAll
            label="Notification recipients"
            :options="notificationUserOptions"
            placeholder="Select notification recipients"
            searchInputPlaceholder="Search by name or email"
            :showSelectAll="false"
        >
            <template #emptyState>
                <DsSelectEmptyState
                    v-if="!addFileStore.branch?.id"
                    icon="fa-regular fa-user-group"
                    subtitle="Select a branch to see recipients"
                    title="Once a branch is selected, available recipients will appear here."
                />
                <DsSelectEmptyState
                    v-else
                    icon="fa-regular fa-user-group"
                    subtitle="Try adjusting your search criteria to find recipients."
                    title="No results match your search."
                />
            </template>
        </DsMultiSelect>

        <div class="mt-6">
            <DsCheckbox
                v-model="addFileStore.notifyClients"
                inputId="notify-add-file"
                label="Send notification to client(s)"
                name="notify"
                value="notify"
            >
            </DsCheckbox>
        </div>

        <!-- @todo [CLENG-1942] remove hidden inputs needed for form submission -->
        <input
            v-for="user in addFileStore.notificationUsers"
            :key="user.id"
            name="fileNotificationUserIds[]"
            type="hidden"
            :value="user.id"
        />
    </AddFileSection>
</template>

<script setup lang="ts">
import { useAddFileStore } from '@/company-portal/add-file/add-file.store';
import { AddFileFormSectionIds } from '@/company-portal/add-file/add-file.types';
import DsSelectEmptyState from '@/components/design-system/DsSelectEmptyState.vue';
import DsCheckbox from '@/components/design-system/form/input/DsCheckbox.vue';
import DsMultiSelect from '@/components/design-system/multiselect/DsMultiSelect.vue';
import AddFileSection from '@/components/Views/Company/AddFile/AddFileSection.vue';
import type { UserWithBranchesGroup } from '@/components/Views/Company/File/file.types';
import { groupUsersByIdWithBranches } from '@/shared/utils/file-notification';
import { useFileNotificationStore } from '@/stores/company/file-notification.store';
import { useCurrentUserStore } from '@/stores/current-user.store';
import { computed, watch } from 'vue';

const fileNotificationStore = useFileNotificationStore();
const addFileStore = useAddFileStore();
const currentUserStore = useCurrentUserStore();

const { users, isFetching } = fileNotificationStore.getNotifiableUsers();

const userSectionState = computed(
    () => new Map<UserWithBranchesGroup['id'], boolean>(addFileStore.notificationUsers.map(user => [user.id, true])),
);

const isLastRemainingRecipientOption = (userId: UserWithBranchesGroup['id']) => {
    return addFileStore.notificationUsers.length === 1 && userSectionState.value.get(userId) === true;
};

const userHasCurrentBranch = (user: UserWithBranchesGroup) => {
    return user.branches.some(branchId => branchId === addFileStore.branch?.id);
};

const notificationUserOptions = computed<UserWithBranchesGroup[]>(() => {
    if (!addFileStore.branch?.id) {
        return [];
    }
    const groupedUsers = groupUsersByIdWithBranches(users.value ?? []);
    return groupedUsers
        .filter(user => userHasCurrentBranch(user))
        .map(user => {
            const lastItemSelected = isLastRemainingRecipientOption(user.id);
            return {
                ...user,
                disabled: lastItemSelected,
                tooltipText: lastItemSelected ? 'At least one notification recipient is required.' : '',
            };
        });
});

const currentUserOption = computed(() =>
    notificationUserOptions.value.find(option => Number(option.id) === currentUserStore.currentUser?.id),
);

/** File creator is added by default so we always have at least one item selected */
const ensureCurrentUserSelected = () => {
    if (addFileStore.notificationUsers.length === 0 && currentUserOption.value) {
        addFileStore.notificationUsers.push(currentUserOption.value);
    }
};

watch(
    () => addFileStore.branch,
    () => {
        addFileStore.notificationUsers = addFileStore.notificationUsers.filter(userHasCurrentBranch);
        ensureCurrentUserSelected();
    },
);

// form submission resets the list, so re-add the current user
watch(() => addFileStore.notificationUsers.length, ensureCurrentUserSelected);
</script>
